import { Link, NavLink, useLocation, useNavigate } from 'react-router-dom';
import { useEffect, useState } from 'react';

const navItems = [
  { label: '首页', to: '/' },
  { label: '电影', to: '/category/movie' },
  { label: '电视剧', to: '/category/tv' },
  { label: '搜索', to: '/search' },
  { label: '关于我们', to: '/about' },
];

export default function SiteHeader() {
  const [drawerOpen, setDrawerOpen] = useState(false);
  const [scrolled, setScrolled] = useState(false);
  const [query, setQuery] = useState('');
  const location = useLocation();
  const navigate = useNavigate();

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 12);
    onScroll();
    window.addEventListener('scroll', onScroll, { passive: true });
    return () => window.removeEventListener('scroll', onScroll);
  }, []);

  useEffect(() => {
    setDrawerOpen(false);
  }, [location.pathname, location.search]);

  useEffect(() => {
    document.body.style.overflow = drawerOpen ? 'hidden' : '';
    return () => {
      document.body.style.overflow = '';
    };
  }, [drawerOpen]);

  const submitSearch = (event: React.FormEvent<HTMLFormElement>) => {
    event.preventDefault();
    const value = query.trim();
    navigate(value ? `/search?q=${encodeURIComponent(value)}` : '/search');
    setDrawerOpen(false);
  };

  return (
    <>
      <header
        className={`sticky top-0 z-40 w-full transition-colors duration-300 ${
          scrolled
            ? 'bg-background-50/95 backdrop-blur border-b border-background-200'
            : 'bg-background-50 border-b border-background-200/60'
        }`}
      >
        <div className="h-0.5 w-full bg-accent-500" />
        <div className="px-4 md:px-8 lg:px-12">
          <div className="flex h-16 md:h-[72px] items-center justify-between gap-4">
            <Link to="/" className="group flex items-center gap-3 shrink-0">
              <span className="flex h-9 w-9 md:h-10 md:w-10 items-center justify-center rounded-lg bg-primary-500 text-background-50 text-lg font-heading font-bold">
                同
              </span>
              <span className="leading-tight">
                <span className="block font-heading text-base md:text-lg font-bold tracking-wide text-foreground-950">
                  同学的妈妈
                </span>
                <span className="block font-label text-[10px] md:text-[11px] uppercase tracking-[0.18em] text-foreground-500">
                  Film Archive
                </span>
              </span>
            </Link>

            <nav className="hidden md:flex items-center gap-1 lg:gap-2">
              {navItems.map((item) => (
                <NavLink
                  key={item.to}
                  to={item.to}
                  end={item.to === '/'}
                  className={({ isActive }) =>
                    `relative px-3 lg:px-4 py-2 text-sm font-medium rounded-md transition-colors cursor-pointer whitespace-nowrap ${
                      isActive
                        ? 'text-accent-600'
                        : 'text-foreground-700 hover:text-primary-600'
                    }`
                  }
                >
                  {({ isActive }) => (
                    <>
                      {item.label}
                      <span
                        className={`absolute left-1/2 -translate-x-1/2 bottom-0 h-0.5 w-4 rounded-full transition-opacity ${
                          isActive ? 'bg-accent-500 opacity-100' : 'opacity-0'
                        }`}
                      />
                    </>
                  )}
                </NavLink>
              ))}
            </nav>

            <div className="flex items-center gap-2">
              <form onSubmit={submitSearch} className="hidden lg:flex items-center">
                <div className="relative">
                  <i className="ri-search-line absolute left-3 top-1/2 -translate-y-1/2 text-foreground-400 text-sm w-4 h-4 flex items-center justify-center" />
                  <input
                    value={query}
                    onChange={(event) => setQuery(event.target.value)}
                    placeholder="搜索影片 / 演员"
                    className="w-52 xl:w-64 h-9 pl-9 pr-3 rounded-md border border-background-300/70 bg-background-100 text-sm text-foreground-900 placeholder:text-foreground-400 outline-none focus:border-primary-400 focus:bg-background-50 transition-colors"
                  />
                </div>
              </form>
              <button
                type="button"
                onClick={() => setDrawerOpen(true)}
                aria-label="打开导航菜单"
                className="md:hidden flex h-9 w-9 items-center justify-center rounded-md border border-background-300/70 text-foreground-800 cursor-pointer"
              >
                <i className="ri-menu-3-line text-lg w-5 h-5 flex items-center justify-center" />
              </button>
            </div>
          </div>
        </div>
      </header>

      {drawerOpen && (
        <div className="fixed inset-0 z-50 md:hidden">
          <button
            type="button"
            aria-label="关闭菜单"
            onClick={() => setDrawerOpen(false)}
            className="absolute inset-0 bg-foreground-950/50 anim-fade-in cursor-pointer"
          />
          <aside className="absolute inset-y-0 right-0 w-[80%] max-w-xs bg-background-50 p-5 anim-fade-in">
            <div className="flex items-center justify-between mb-6">
              <span className="font-heading text-lg font-bold text-foreground-950">同学的妈妈</span>
              <button
                type="button"
                onClick={() => setDrawerOpen(false)}
                aria-label="关闭菜单"
                className="flex h-9 w-9 items-center justify-center rounded-md bg-background-100 text-foreground-700 cursor-pointer"
              >
                <i className="ri-close-line text-lg w-5 h-5 flex items-center justify-center" />
              </button>
            </div>
            <form onSubmit={submitSearch} className="mb-5">
              <div className="relative">
                <i className="ri-search-line absolute left-3 top-1/2 -translate-y-1/2 text-foreground-400 text-sm w-4 h-4 flex items-center justify-center" />
                <input
                  value={query}
                  onChange={(event) => setQuery(event.target.value)}
                  placeholder="搜索影片 / 演员"
                  className="w-full h-10 pl-9 pr-3 rounded-md border border-background-300/70 bg-background-100 text-sm text-foreground-900 placeholder:text-foreground-400 outline-none focus:border-primary-400"
                />
              </div>
            </form>
            <nav className="flex flex-col gap-1">
              {navItems.map((item) => (
                <NavLink
                  key={item.to}
                  to={item.to}
                  end={item.to === '/'}
                  className={({ isActive }) =>
                    `flex items-center justify-between px-3 py-3 rounded-md text-sm font-medium ${
                      isActive
                        ? 'bg-primary-50 text-primary-700'
                        : 'text-foreground-800 hover:bg-background-100'
                    }`
                  }
                >
                  {item.label}
                  <i className="ri-arrow-right-s-line text-foreground-400 w-4 h-4 flex items-center justify-center" />
                </NavLink>
              ))}
            </nav>
            <p className="mt-8 text-xs leading-relaxed text-foreground-500">
              本站仅提供影视资料与剧情介绍，不提供播放与下载。
            </p>
          </aside>
        </div>
      )}
    </>
  );
}